declare global {
  interface Window {
    adBreak?: (options: Record<string, unknown>) => void;
    adConfig?: (options: Record<string, unknown>) => void;
  }
}
export interface RewardVideoState {
  mode?: string; phase?: string; videoToken?: string; videoKind?: number;
  videoReady?: boolean; videoShowing?: boolean; videoCompleted?: number;
  counterSelecting?: boolean; rewardOpen?: boolean;
}

/** Optional H5 rewarded inventory. No fake completion, display-unit substitution or mobile SDK. */
export class RewardedAds {
  private enabled = import.meta.env.VITE_REWARDED_ADS_ENABLED === 'true';
  private apiReady = false;
  private state: RewardVideoState = {};
  private prepared = '';
  private clicked = '';
  private showAd?: () => void;
  private generation = 0;
  private announcedAvailable = '';
  constructor(private send: (method: string, json: string) => void,
    private playing: () => boolean) {
    if (!this.enabled) return;
    window.adBreak ??= options => { (window.adsbygoogle ??= []).push(options); };
    window.adConfig ??= options => { (window.adsbygoogle ??= []).push(options); };
    window.adConfig({ preloadAdBreaks: 'on', onReady: () => { this.apiReady = true; this.update(this.state); } });
  }
  /** Called only after Unity's in-game button requests the current offer. */
  request(requestedToken?: string) {
    const token = this.state.videoToken;
    if (!this.enabled || !token || requestedToken !== token || !this.showAd || !this.state.videoReady ||
      !this.playing() || this.clicked || this.state.counterSelecting || this.state.rewardOpen) return;
    this.clicked = token; this.emit(token, 'show'); this.render();
    window.setTimeout(() => {
      if (this.clicked === token && !this.state.videoShowing) { this.clicked = ''; this.render(); }
    }, 750);
  }
  update(state: RewardVideoState) {
    this.state = state;
    if (!this.enabled) return;
    const token = state.videoToken ?? '';
    if (this.prepared === token && state.videoShowing && this.showAd) {
      const show = this.showAd; this.showAd = undefined; this.clicked = '';
      try { show(); } catch { this.emit(token, 'failed'); }
    }
    if (!token || state.mode !== 'challenge' || state.phase === 'home' || state.phase === 'over') {
      if (this.prepared) { this.generation++; this.prepared = ''; this.showAd = undefined; this.clicked = ''; }
      this.render(); return;
    }
    if (this.apiReady && token !== this.prepared && !state.videoShowing) this.prepare(token);
    this.render();
  }
  private emit(token: string, action: string) { this.send('RewardVideoEvent', JSON.stringify({ token, action })); }
  private prepare(token: string) {
    this.prepared = token; this.showAd = undefined; this.clicked = '';
    const generation = ++this.generation;
    const live = () => generation === this.generation;
    try {
      window.adBreak!({ type: 'reward', name: 'challenge-tool',
        beforeReward: (showAd: () => void) => {
          if (!live()) return;
          this.showAd = showAd; this.emit(token, 'ready'); this.render();
        },
        beforeAd: () => { if (live()) { this.announcedAvailable = ''; this.emit(token, 'unavailable'); } },
        // The host checks unique run leases, including viewed after closed.
        adViewed: () => { this.emit(token, 'viewed'); },
        adDismissed: () => { this.emit(token, 'closed'); },
        afterAd: () => { this.emit(token, 'closed'); if (live()) { this.showAd = undefined; this.render(); } },
        adBreakDone: (details: { breakStatus?: string }) => {
          if (!live()) return;
          if (details.breakStatus !== 'viewed' && details.breakStatus !== 'dismissed') this.emit(token, 'failed');
          this.showAd = undefined; this.clicked = ''; this.render();
        }
      });
    } catch { this.emit(token, 'failed'); }
  }
  private render() {
    const visible = this.enabled && this.playing() && this.state.mode === 'challenge' &&
      !!this.state.videoReady && !!this.showAd && !this.state.videoShowing &&
      !this.state.counterSelecting && !this.state.rewardOpen;
    const available = visible && !this.clicked ? this.state.videoToken ?? '' : '';
    if (available === this.announcedAvailable) return;
    const previous = this.announcedAvailable; this.announcedAvailable = available;
    if (available) this.emit(available, 'available');
    else if (previous) this.emit(previous, 'unavailable');
  }
}
